import { useCallback, useEffect, useMemo, useState } from 'react';
import type { WeldDrillDownRow } from '../components/dashboard/drilldown/drillDownTypes';
import type { DashboardMetrics } from './weldDashboardMetrics';
import { rowKey } from './weldDrillDownModel';
import {
  collectViolationsForWeld,
  fetchPassViewRecords,
  fetchProjectLimitsApi,
  fetchTiltViewRecords,
  fetchZoneViewRecords,
} from './fetchWeldViewApis';
import { runWithConcurrency } from './runWithConcurrency';
import type { LimitsRow, WpsViolationRecord } from './wpsViolationModel';
import {
  aggregateCorrectiveHints,
  aggregateFailuresByParameter,
  computeProductionImpact,
  findSimilarWelds,
} from './wpsViolationModel';

/** Keep overview sampling from stampeding Limits /view/* (see limitsViewFetchQueue). */
const OVERVIEW_WELD_CONCURRENCY = 2;

export interface WpsViolationOverviewData {
  parameterBreakdown: { parameter: string; count: number }[];
  productionImpact: ReturnType<typeof computeProductionImpact>;
  correctiveHints: { hint: string; count: number }[];
  similarWeldsPreview: ReturnType<typeof findSimilarWelds>;
  complianceTrend: { label: string; passRatePct: number }[];
  loading: boolean;
  violationsByRowKey: Record<string, WpsViolationRecord[]>;
}

export function useWpsViolationOverview(
  metrics: DashboardMetrics | null,
  failedRows: WeldDrillDownRow[],
  projectId: number | null,
  enabled: boolean,
): WpsViolationOverviewData {
  const [loading, setLoading] = useState(false);
  const [violationsByRowKey, setViolationsByRowKey] = useState<Record<string, WpsViolationRecord[]>>({});

  const load = useCallback(async () => {
    if (!enabled || !projectId || failedRows.length === 0) {
      setViolationsByRowKey({});
      return;
    }

    setLoading(true);
    try {
      const limitsData = await fetchProjectLimitsApi(projectId);

      const cache: Record<string, WpsViolationRecord[]> = {};
      const sample = failedRows.slice(0, 15);

      await runWithConcurrency(sample, OVERVIEW_WELD_CONCURRENCY, async (row) => {
        const key = rowKey(row);
        try {
          const params = {
            project_id: projectId,
            project_name: row.projectName || undefined,
            weld_number: row.weldNumber,
            job_number: row.jobNumber || undefined,
          };
          const [passes, zones, tilts] = await Promise.all([
            fetchPassViewRecords(params),
            fetchZoneViewRecords(params),
            fetchTiltViewRecords(params),
          ]);
          cache[key] = collectViolationsForWeld(passes, zones, tilts, limitsData.allDataSet);
        } catch {
          cache[key] = [];
        }
      });

      setViolationsByRowKey(cache);
    } finally {
      setLoading(false);
    }
  }, [enabled, projectId, failedRows]);

  useEffect(() => {
    load();
  }, [load]);

  return useMemo(() => {
    const allViolations = Object.values(violationsByRowKey).flat();
    const parameterBreakdown = aggregateFailuresByParameter(allViolations);
    const productionImpact = computeProductionImpact(failedRows, violationsByRowKey);
    const correctiveHints = aggregateCorrectiveHints(allViolations);

    const firstFailed = failedRows[0];
    const similarWeldsPreview = firstFailed
      ? findSimilarWelds(failedRows, firstFailed, violationsByRowKey, 3)
      : [];

    const welds = metrics?.welds ?? [];
    const windowSize = Math.max(3, Math.floor(welds.length / 5));
    const complianceTrend: { label: string; passRatePct: number }[] = [];
    for (let i = 0; i < welds.length; i += windowSize) {
      const chunk = welds.slice(i, i + windowSize);
      const passed = chunk.filter((w) => w.outcome === 'pass').length;
      complianceTrend.push({
        label: chunk[0]?.weldNumber ?? `W${i + 1}`,
        passRatePct: chunk.length ? (passed / chunk.length) * 100 : 0,
      });
    }

    return {
      parameterBreakdown,
      productionImpact,
      correctiveHints,
      similarWeldsPreview,
      complianceTrend,
      loading,
      violationsByRowKey,
    };
  }, [violationsByRowKey, failedRows, metrics, loading]);
}

/**
 * Failed-params cell for out-of-limit welds.
 * - Not fetched yet → "—"
 * - Loaded with param breaches → joined labels (e.g. "Heat input")
 * - Loaded with no per-param breaches → "Overall WPS"
 */
export function failedParamSummaryForRow(
  row: WeldDrillDownRow,
  violationsByRowKey: Record<string, WpsViolationRecord[]>,
): string {
  const key = rowKey(row);
  if (!(key in violationsByRowKey)) {
    return '—';
  }
  const violations = violationsByRowKey[key] ?? [];
  const params = Array.from(new Set(violations.map((v) => v.parameterLabel)));
  if (params.length > 0) {
    return params.join(', ');
  }
  if (row.outcome === 'fail') {
    return 'Overall WPS';
  }
  return '—';
}

export function worstDeviationForRow(
  row: WeldDrillDownRow,
  violationsByRowKey: Record<string, WpsViolationRecord[]>,
): string {
  const pcts = (violationsByRowKey[rowKey(row)] ?? [])
    .map((v) => v.deviationPct)
    .filter((p): p is number => p != null && Number.isFinite(p));
  if (!pcts.length) return '—';
  return `${Math.max(...pcts).toFixed(1)}%`;
}
